import React, { useState, useEffect } from "react";
import {
  Alert,
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Container,
} from "@mui/material";
import SortablePriorityList from "../parts/SortablePriorityList";
import RequiredLabel from "../../shared/RequiredLabel";
import { formatJpDateTime } from "../../utils/dateUtils";

const ProjectRequestEdit = (props) => {
  const { userContractRequest, priorities, csrfToken, formAction } = props;
  const [errors, setErrors] = useState([]);
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [formState, setFormState] = useState({
    desired_project: userContractRequest?.desired_project || "",
    desired_amount: userContractRequest?.desired_amount || "",
    start_date: userContractRequest?.start_date || "",
    technical_skill: userContractRequest?.technical_skill || "",
    remark: userContractRequest?.remark || "",
    work_style: userContractRequest?.work_style || "on_site",
  });

  // エラーが出たらページ上部にスクロール
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered]);

  // 指定された日付が過去かを判定（空文字は除外）
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  // 数値が空または負の値か
  const isEmptyOrNegative = (value) => {
    const num = Number(value);
    return value === "" || isNaN(num) || num < 0;
  };

  // 空文字・null・undefined を空とみなす
  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  // フィールド別のエラーフラグ（赤枠表示用）
  const isInvalidDesiredProject =
    validationTriggered && isEmpty(formState.desired_project);
  const isInvalidTechnicalSkill =
    validationTriggered && isEmpty(formState.technical_skill);
  const isInvalidStartDate =
    validationTriggered &&
    (isEmpty(formState.start_date) || isDateInPast(formState.start_date));
  const isInvalidDesiredAmount =
    validationTriggered &&
    (isEmpty(formState.desired_amount) ||
      isEmptyOrNegative(formState.desired_amount));

  const validateForm = () => {
    const newErrors = [];

    const rules = [
      {
        field: "desired_project",
        isError: isEmpty(formState.desired_project),
        message: "希望プロジェクトは入力必須です。",
      },
      {
        field: "desired_amount",
        isError: isEmpty(formState.desired_amount),
        message: "希望金額は入力必須です。",
      },
      {
        field: "desired_amount",
        isError:
          !isEmpty(formState.desired_amount) &&
          isEmptyOrNegative(formState.desired_amount),
        message: "希望金額は0以上を入力してください。",
      },
      {
        field: "start_date",
        isError: isEmpty(formState.start_date),
        message: "希望開始時期は入力必須です。",
      },
      {
        field: "start_date",
        isError:
          !isEmpty(formState.start_date) && isDateInPast(formState.start_date),
        message: "希望開始時期は今日以降の日付を指定してください。",
      },
      {
        field: "technical_skill",
        isError: isEmpty(formState.technical_skill),
        message: "得意スキルは入力必須です。",
      },
    ];

    rules.forEach((rule) => {
      if (rule.isError) {
        newErrors.push(rule.message);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = (e) => {
    if (!validateForm()) {
      e.preventDefault();
      setValidationTriggered(true);
      return;
    }
  };

  const handleChange = (e) => {
    setValidationTriggered(false);
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleWorkStyleChange = (_, newValue) => {
    if (newValue !== null) {
      setValidationTriggered(false);
      setFormState({ ...formState, work_style: newValue });
    }
  };

  const characterCount = formState.remark ? formState.remark.length : 0;

  return (
    <Container maxWidth="md" sx={{ position: "relative", mt: 2, mb: 10 }}>
      <Button
        onClick={() => window.history.back()}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>

      <form
        id="user-contract-request-form"
        action={formAction}
        method="post"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="_method" value="patch" />
        <input type="hidden" name="authenticity_token" value={csrfToken} />

        {Object.entries(formState).map(([key, value]) => (
          <input
            key={key}
            type="hidden"
            name={`user_contract_request[${key}]`}
            value={value}
          />
        ))}

        <Box sx={{ bgcolor: "#fff", p: 4, borderRadius: 2, boxShadow: 1 }}>
          {validationTriggered && errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.map((error, idx) => (
                <div key={idx}>{error}</div>
              ))}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              プロジェクトの募集要項
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                borderBottom: "1px solid #ccc",
                pb: 1,
              }}
            >
              更新日時：{formatJpDateTime(userContractRequest?.updated_at)}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望プロジェクト</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="desired_project"
                value={formState.desired_project}
                onChange={handleChange}
                placeholder="React/Rails案件"
                error={isInvalidDesiredProject}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望金額（税別）</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="desired_amount"
                type="number"
                value={formState.desired_amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholder="70"
                error={isInvalidDesiredAmount}
                sx={{
                  "& .MuiOutlinedInput-root input": {
                    boxShadow: "none",
                    outline: "none",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">万円</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望開始時期</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="start_date"
                type="date"
                value={formState.start_date}
                onChange={handleChange}
                error={isInvalidStartDate}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>稼働形態</Typography>
                <RequiredLabel show={true} />
              </Box>
              <ToggleButtonGroup
                exclusive
                value={formState.work_style}
                onChange={handleWorkStyleChange}
                fullWidth
                size="small"
              >
                <ToggleButton value="on_site">常駐</ToggleButton>
                <ToggleButton value="remote">フルリモート</ToggleButton>
                <ToggleButton value="hybrid">ハイブリッド</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>得意スキル</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="technical_skill"
                value={formState.technical_skill}
                onChange={handleChange}
                placeholder="React、Rails、詳細設計"
                error={isInvalidTechnicalSkill}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                備考(最大200文字まで)
              </Typography>
              <TextField
                fullWidth
                name="remark"
                value={formState.remark}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="補足事項や特記事項をご記入ください"
                sx={{ "& textarea:focus": { boxShadow: "none" } }}
                helperText={`${characterCount}文字`}
                inputProps={{ maxLength: 200 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: "0",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <SortablePriorityList
                defaultOrder={Array.isArray(priorities) ? priorities : []}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              right: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/user_contract_request")}
              sx={{
                backgroundColor: "#ffffff",
                color: "#000",
                borderRadius: 9999,
                px: 4,
                fontWeight: "bold",
                border: "1px solid #9ca3af",
                "&:hover": {
                  opacity: 0.9,
                  backgroundColor: "#f3f4f6",
                  borderColor: "#9ca3af",
                },
                textAlign: "center",
              }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              sx={{
                borderRadius: 4,
                color: "#fff",
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default ProjectRequestEdit;
